import React from 'react';
import { Dropdown, Menu, Popover } from 'antd';
import { AppstoreOutlined, CloseOutlined, DashboardOutlined } from '@ant-design/icons';
import {
    DrawerOverlay,
    MobileDrawer,
    DrawerContent,
    DrawerHeader,
    DrawerCloseButton,
    DrawerSidebarRow,
    DrawerModuleSidebarBlack,
    DrawerFeatureSidebarPanel,
    DrawerFeatureSidebarScroll,
    ModuleSidebarNavColumn,
    ModuleSidebarMoreTrigger,
    ModuleMorePopoverGrid,
    ModuleMoreGridTile,
    UserAvatar,
} from './style';
import AccountAvatarFace from '@/components/ui/AccountAvatarFace';
import {
    ProjectFeatureSidebarHeader,
} from '@/components/module/projects/projectFeatureSidebar';

/**
 * Mobile sidebar drawer.
 * Keeps layout/markup here so `Inside.jsx` stays easy to maintain.
 */
export function MobileSidebarDrawer({
    isOpen,
    onClose,
    mode,
    moduleBarWidth,
    currentUserAvatarColor,
    currentUserAvatarUrl,
    getUserInitials,
    getUserName,
    getUserRole,
    userDropdownOpen,
    setUserDropdownOpen,
    userMenuItems,
    handleUserMenuClick,
    primaryModuleSelectedKeys,
    primaryModuleMenuItems,
    moreModuleItems,
    moreModulesOpen,
    setMoreModulesOpen,
    isMoreModuleActive,
    moduleId,
    handleMenuClick,
    projectIdFromPath,
    projectDetailIsError,
    projectHeaderDisplayName,
    featureSidebarTitle,
    selectedKeys,
    featureSidebarItems,
    featureSidebarSelectedKeys,
    projectSidebarDisplayItems,
}) {
    return (
        <>
            <DrawerOverlay $isOpen={isOpen} onClick={onClose} />
            <MobileDrawer $isOpen={isOpen}>
                <DrawerContent>
                    <DrawerHeader>
                        <Dropdown
                            menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
                            placement="bottomLeft"
                            trigger={['click']}
                            overlayStyle={{ minWidth: 260 }}
                            open={userDropdownOpen}
                            onOpenChange={setUserDropdownOpen}
                        >
                            <button
                                type="button"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    padding: 0,
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    minWidth: 0,
                                }}
                            >
                                <UserAvatar
                                    $collapsed={false}
                                    $size="36px"
                                    $radius="10px"
                                    $avatarBg={currentUserAvatarUrl ? 'transparent' : currentUserAvatarColor?.bg}
                                    $avatarColor={currentUserAvatarUrl ? 'transparent' : currentUserAvatarColor?.color}
                                    style={{
                                        border: currentUserAvatarUrl ? '1px solid var(--ant-color-border-secondary, #e5e7eb)' : 'none',
                                    }}
                                >
                                    <AccountAvatarFace
                                        imageUrl={currentUserAvatarUrl}
                                        initials={getUserInitials?.()}
                                        alt={getUserName?.()}
                                    />
                                </UserAvatar>
                                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                    <div
                                        style={{
                                            fontWeight: 700,
                                            fontSize: 14,
                                            lineHeight: 1.2,
                                            color: mode === 'dark' ? '#fff' : '#111',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: 220,
                                        }}
                                    >
                                        {getUserName?.()}
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 12,
                                            lineHeight: 1.2,
                                            color: mode === 'dark' ? 'rgba(255,255,255,0.55)' : '#6b7280',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: 220,
                                        }}
                                    >
                                        {getUserRole?.()}
                                    </div>
                                </div>
                            </button>
                        </Dropdown>
                        <DrawerCloseButton onClick={onClose}>
                            <CloseOutlined />
                        </DrawerCloseButton>
                    </DrawerHeader>

                    <DrawerSidebarRow>
                        <DrawerModuleSidebarBlack $width={moduleBarWidth}>
                            <ModuleSidebarNavColumn>
                                <Menu
                                    mode="inline"
                                    motion={false}
                                    selectedKeys={primaryModuleSelectedKeys}
                                    items={primaryModuleMenuItems}
                                    onClick={handleMenuClick}
                                    style={{ border: 'none', background: 'transparent' }}
                                />
                                {moreModuleItems?.length > 0 && (
                                    <Popover
                                        placement="rightTop"
                                        trigger="click"
                                        open={moreModulesOpen}
                                        onOpenChange={setMoreModulesOpen}
                                        content={
                                            <ModuleMorePopoverGrid>
                                                {moreModuleItems.map((m) => (
                                                    <ModuleMoreGridTile
                                                        key={m.id}
                                                        type="button"
                                                        $active={moduleId === m.id}
                                                        onClick={() => handleMenuClick({ key: m.key })}
                                                    >
                                                        {m.icon ? (
                                                            React.isValidElement(m.icon) ? (
                                                                m.icon
                                                            ) : (
                                                                React.createElement(m.icon)
                                                            )
                                                        ) : (
                                                            <DashboardOutlined />
                                                        )}
                                                        <span>{String(m.label).replace(/\n/g, ' ')}</span>
                                                    </ModuleMoreGridTile>
                                                ))}
                                            </ModuleMorePopoverGrid>
                                        }
                                        styles={{ body: { padding: 12, borderRadius: 12 } }}
                                    >
                                        <ModuleSidebarMoreTrigger
                                            type="button"
                                            $active={isMoreModuleActive}
                                            aria-label="More modules"
                                        >
                                            <AppstoreOutlined />
                                            <span>More</span>
                                        </ModuleSidebarMoreTrigger>
                                    </Popover>
                                )}
                            </ModuleSidebarNavColumn>
                        </DrawerModuleSidebarBlack>

                        <DrawerFeatureSidebarPanel>
                            <DrawerFeatureSidebarScroll>
                                {projectIdFromPath ? (
                                    <>
                                        <ProjectFeatureSidebarHeader
                                            title={projectHeaderDisplayName}
                                            isError={projectDetailIsError}
                                        />
                                        {projectSidebarDisplayItems.length > 0 ? (
                                            <Menu
                                                mode="inline"
                                                motion={false}
                                                selectedKeys={featureSidebarSelectedKeys}
                                                items={projectSidebarDisplayItems}
                                                onClick={handleMenuClick}
                                                style={{ border: 'none', background: 'transparent' }}
                                            />
                                        ) : null}
                                    </>
                                ) : (
                                    <>
                                        <div className="drawer-feature-title">{featureSidebarTitle}</div>
                                        <Menu
                                            mode="inline"
                                            motion={false}
                                            selectedKeys={selectedKeys}
                                            items={featureSidebarItems}
                                            onClick={handleMenuClick}
                                            style={{ border: 'none', background: 'transparent' }}
                                        />
                                    </>
                                )}
                            </DrawerFeatureSidebarScroll>
                        </DrawerFeatureSidebarPanel>
                    </DrawerSidebarRow>
                </DrawerContent>
            </MobileDrawer>
        </>
    );
}

